/**
 * 体脂肪率 実測 vs 期待推移チャート
 *
 * 実測BF（Eufy測定）と、摂取カロリーから算出した期待BF推移を重ねて表示。
 * 乖離が大きいほど「計画からずれている」ことが視覚的にわかる。
 */

import { format, parseISO } from 'date-fns'
import { ja } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { getBfProjection } from '@/api/services/eufy'
import type { BfProjectionResponse } from '@/api/types/eufy'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

type Props = {
  days: number
}

type ChartRow = {
  date: string
  dateLabel: string
  actual_bf: number | null
  expected_bf: number | null
}

export function BfProjectionChart({ days }: Props) {
  const [data, setData] = useState<BfProjectionResponse | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let ignore = false
    setLoading(true)
    getBfProjection(days)
      .then((res) => {
        if (!ignore) setData(res)
      })
      .catch(() => {
        if (!ignore) setData(null)
      })
      .finally(() => {
        if (!ignore) setLoading(false)
      })
    return () => {
      ignore = true
    }
  }, [days])

  if (loading) {
    return (
      <Card className="p-4">
        <Skeleton className="h-50 w-full" />
      </Card>
    )
  }

  if (!data || !data.start_bf || data.data_points.length === 0) {
    return (
      <Card className="p-4">
        <p className="text-muted-foreground text-xs text-center">
          体脂肪率データがありません（期間内にBF測定が必要です）
        </p>
      </Card>
    )
  }

  const chartData: ChartRow[] = data.data_points.map((p) => ({
    date: p.date,
    dateLabel: format(parseISO(p.date), 'M/d', { locale: ja }),
    actual_bf: p.actual_bf,
    expected_bf: p.expected_bf,
  }))

  // Y軸の範囲を算出
  const allValues = chartData.flatMap((r) => [r.actual_bf, r.expected_bf]).filter((v): v is number => v !== null)
  if (allValues.length === 0) {
    return (
      <Card className="p-4">
        <p className="text-muted-foreground text-xs text-center">表示可能なデータがありません</p>
      </Card>
    )
  }
  const minY = Math.floor(Math.min(...allValues) * 2) / 2 - 0.5
  const maxY = Math.ceil(Math.max(...allValues) * 2) / 2 + 0.5

  // 目標変更日のラベル（X軸上の位置特定用）
  const goalChangedLabel = data.goal_changed_at ? format(parseISO(data.goal_changed_at), 'M/d', { locale: ja }) : null

  return (
    <Card className="space-y-2 p-4">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-medium">体脂肪率: 実測 vs 期待推移</h3>
        <div className="flex gap-3 text-[10px] text-muted-foreground">
          {data.maintenance_avg && <span>維持: {data.maintenance_avg.toFixed(0)} kcal</span>}
          {data.intake_avg && <span>平均摂取: {data.intake_avg.toFixed(0)} kcal</span>}
        </div>
      </div>

      <div className="h-50 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis
              dataKey="dateLabel"
              tick={{ fontSize: 10 }}
              interval={days <= 7 ? 0 : days <= 30 ? 4 : Math.floor(days / 8)}
            />
            <YAxis domain={[minY, maxY]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
            <Tooltip content={<BfTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '11px' }}
              formatter={(value: string) => (value === 'actual_bf' ? '実測' : '期待（摂取ベース）')}
            />
            <ReferenceLine y={data.start_bf} stroke="#666" strokeDasharray="2 2" opacity={0.5} />
            {goalChangedLabel && (
              <ReferenceLine
                x={goalChangedLabel}
                stroke="#10b981"
                strokeDasharray="4 2"
                strokeWidth={1.5}
                label={{ value: '目標変更', position: 'top', fontSize: 9, fill: '#10b981' }}
              />
            )}
            <Line
              type="monotone"
              dataKey="actual_bf"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ r: 2.5 }}
              connectNulls={false}
              name="actual_bf"
            />
            <Line
              type="monotone"
              dataKey="expected_bf"
              stroke="#f97316"
              strokeWidth={1.5}
              strokeDasharray="5 3"
              dot={false}
              connectNulls
              name="expected_bf"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  )
}

function BfTooltip({ active, payload, label }: { active?: boolean; payload?: unknown[]; label?: string }) {
  if (!active || !payload || payload.length === 0) return null

  const items = payload as { dataKey: string; value: number | null; color: string }[]
  const actual = items.find((i) => i.dataKey === 'actual_bf')
  const expected = items.find((i) => i.dataKey === 'expected_bf')

  const diff = actual?.value != null && expected?.value != null ? (actual.value - expected.value).toFixed(2) : null

  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
      <p className="mb-1 font-medium">{label}</p>
      {actual?.value != null && <p style={{ color: actual.color }}>実測: {actual.value.toFixed(2)}%</p>}
      {expected?.value != null && <p style={{ color: expected.color }}>期待: {expected.value.toFixed(3)}%</p>}
      {diff && (
        <p className="mt-1 border-t pt-1 text-muted-foreground">
          乖離: {Number(diff) > 0 ? '+' : ''}
          {diff}%
        </p>
      )}
    </div>
  )
}
